import React, {useEffect, useState} from 'react'
import {ActionButtons, CustomModal, InputLabel, TextAreaInput} from '../../../common'
import {i18n} from '../../../../i18n'
import {sharedStyles} from '../../../../style/shared_styles'
import {TherapyInterruptReason, TherapyStatus} from '../../../../utils/enums'
import {useStores} from '../../../../store'
import VSpace from '../../../common/VSpace'
import {observer} from 'mobx-react'
import {Typography} from 'antd'

const decideInterruptReason = (status: TherapyStatus): TherapyInterruptReason => {
    return status === TherapyStatus.RELEASED ? TherapyInterruptReason.CANCELLED : TherapyInterruptReason.DISCARDED
}

/**
 * Component representing the therapy archive modal.
 *
 * For a real therapy cancellation (status RELEASED -> CANCELLED) the user must enter a
 * free-text reason, which is persisted together with the cancellation date. For the other
 * cases (e.g. discarding a therapy) only the computed standard reason is displayed, and
 * nothing is written to the cancellation fields.
 */
const ArchiveModal: React.FC<ArchiveModalProps> = observer((props) => {
    const [therapyInterruptReason, setTherapyInterruptReason] = useState<TherapyInterruptReason>()
    const [cancelReason, setCancelReason] = useState<string | undefined>('')
    const {therapyStore} = useStores()

    const isCancellation = therapyInterruptReason === TherapyInterruptReason.CANCELLED

    useEffect(() => {
        setTherapyInterruptReason(decideInterruptReason(props.status))
    }, [props.status])

    const doArchive = async () => {
        // Only a real cancellation persists a free-text reason; discarding sends none.
        await therapyStore.closeTherapy(isCancellation ? (cancelReason ?? '') : '')
        closeModal()
        props.onSave?.()
    }

    const closeModal = () => {
        setCancelReason('')
        props.setVisible(false)
    }
    if (!therapyInterruptReason) {
        return <></>
    }

    return (
        <CustomModal open={props.visible} title={i18n.t('therapyTab.basicSection.archive.title')}>
            <div style={isCancellation ? sharedStyles.column : {...sharedStyles.column, alignItems: 'center'}}>
                {isCancellation ? (
                    <TextAreaInput
                        label={i18n.t('therapyTab.basicSection.archive.reason')}
                        mandatory={true}
                        value={cancelReason}
                        onValueChanged={setCancelReason}
                        rows={4}
                    />
                ) : (
                    <>
                        <InputLabel label={i18n.t('therapyTab.basicSection.archive.reason')} />
                        <Typography.Text keyboard={true} style={{fontSize: 18, marginTop: 10}}>
                            {i18n.t(`therapy.interruptReason.${therapyInterruptReason}`)}
                        </Typography.Text>
                    </>
                )}
                <VSpace size={25} />
                <ActionButtons onCancel={closeModal} onSave={doArchive} disabledSave={isCancellation && !cancelReason?.trim()} />
            </div>
        </CustomModal>
    )
})

type ArchiveModalProps = {
    visible: boolean,
    setVisible: (state: boolean) => void,
    status: TherapyStatus
    onSave?: () => void
}

export default ArchiveModal
